import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@opendate-app/setlist';
import SalesDetailPage from './SalesDetailPage';
import {
  TOAST_SYNC_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/toastSyncColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { buildToastSyncFilterGroups } from '../constants/toastSyncFiltersConfig';
import { toastSuccess, toastError, errorText } from '../../../shared/toastHelper';

const axios = require('axios').default;

const STATUS_BADGES = {
  synced: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  missing_file: { bg: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)' },
  unmatched: { bg: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)' },
  not_posted: { bg: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)' },
  out_of_sync: { bg: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)' },
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.status && activeFilters.status.length > 0) {
    params.status = activeFilters.status;
  }
  return params;
};

const TOAST_SYNC_CONFIG = {
  pageTitle: 'Toast Sync',
  pageSubtitle: 'Whether each show’s Toast sales reached its P&L, and where the chain broke.',
  sidebarKey: 'food_beverage_toast_sync',
  defaultPreset: 'last_30_days',

  columns: TOAST_SYNC_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingToastSyncColumns_${teamId}`,
  firstColumnId: 'event_date',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/food-and-beverage/toast-sync/data.json`,
  detailResponseKey: 'toast_sync_rows',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/food-and-beverage/toast-sync/export`,
  salesExportSection: 'toast_sync',
  detailErrorMessage: 'Failed to load Toast sync data.',
  detailTableTitle: 'Shows',
  emptyMessage: 'No shows with Toast-enabled venues in this range.',
  searchPlaceholder: 'Search shows...',
  paginationLabel: 'shows',
  itemLabel: (count) => `show${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    {
      label: 'Shows Synced',
      value: (current.synced || 0).toLocaleString(),
      current: current.synced || 0,
      prior: prior.synced || 0,
    },
    {
      label: 'Needs Attention',
      value: (current.needs_attention || 0).toLocaleString(),
      current: current.needs_attention || 0,
      prior: prior.needs_attention || 0,
    },
    {
      label: 'Toast Net Sales',
      value: compactCurrency(current.toast_net_sales || 0),
      current: current.toast_net_sales || 0,
      prior: prior.toast_net_sales || 0,
    },
    {
      label: 'Posted to P&L',
      value: compactCurrency(current.pnl_posted || 0),
      current: current.pnl_posted || 0,
      prior: prior.pnl_posted || 0,
    },
  ],
  kpiGridColumns: 4,

  chartColor: 'var(--sl-fg-brand, #1982C4)',
  overTimeKey: null,

  filterTitle: 'Filter Shows',
  filterSubtitle: 'Narrow by venue or sync status.',
};

const FeedHealthNotice = ({ feedHealth }) => {
  const stale = (feedHealth || []).filter(venue => venue.stale);
  if (stale.length === 0) return null;

  return (
    <div className="alert alert-warning" role="alert">
      <strong>Toast feed gap detected.</strong>
      <ul className="mb-0 mt-2 pl-4">
        {stale.map(venue => (
          <li key={venue.venue_ownership_id}>
            {venue.venue_name}
            {venue.last_business_date
              ? ` — last file received for ${venue.last_business_date} (${venue.days_since} days ago)`
              : ' — no Toast file has ever been received'}
          </li>
        ))}
      </ul>
      <div className="mt-2 small">
        Shows in this range marked <em>No Toast file</em> are waiting on the feed, not on a
        setting in Opendate. Re-syncing will not populate them until the file arrives.
      </div>
    </div>
  );
};

function ReportingFoodAndBeverageToastSyncPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});
  const [syncState, setSyncState] = useState({});
  const [importState, setImportState] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    axios.get(`/teams/${team.id}/reporting/food-and-beverage/toast-sync/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((error) => console.error('Failed to load Toast sync filter options', error));
  }, [team?.id]);

  const resync = useCallback((calendarEventId) => {
    setSyncState(prev => ({ ...prev, [calendarEventId]: 'pending' }));

    axios.post(`/teams/${team.id}/confirms/${calendarEventId}/toast_syncs.json`)
      .then(() => setSyncState(prev => ({ ...prev, [calendarEventId]: 'queued' })))
      .catch((error) => {
        console.error('Failed to queue Toast re-sync', error);
        setSyncState(prev => ({ ...prev, [calendarEventId]: 'failed' }));
      });
  }, [team?.id]);

  const importFile = useCallback((row) => {
    const key = `${row.venue_ownership_id}-${row.toast_date_path}`;
    setImportState(prev => ({ ...prev, [key]: 'pending' }));

    axios.post(
      `/teams/${team.id}/toast_file_imports`,
      { venue_ownership_id: row.venue_ownership_id, date_path: row.toast_date_path },
      { headers: csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {} }
    )
      .then((res) => {
        const queued = (res.data?.imported_date_paths || []).length > 0;
        setImportState(prev => ({ ...prev, [key]: queued ? 'queued' : 'none' }));
        if (queued) {
          toastSuccess(`Pulling the Toast file for ${row.venue_name}. Refresh in a minute to see it.`);
        } else {
          toastSuccess(`Nothing to pull for ${row.venue_name} on that day.`);
        }
      })
      .catch((error) => {
        setImportState(prev => ({ ...prev, [key]: 'failed' }));
        toastError(errorText(error, 'Could not pull the Toast file.'));
      });
  }, [team?.id, csrfToken]);

  const configWithHandlers = {
    ...TOAST_SYNC_CONFIG,
    buildFilterGroups: () => buildToastSyncFilterGroups(filterOptions),
    renderNotice: () => <FeedHealthNotice feedHealth={filterOptions.feed_health} />,
    renderCellValue: (col, row, { formatEventDate, formatEventDateTime, currencyFormatter: cf }) => {
      switch (col.id) {
        case 'event_date':
          return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '—'}</span>;
        case 'event_name':
          return (
            <a href={`/teams/${team.id}/confirms/${row.id}/profit_and_loss`}>
              {row.event_name || '—'}
            </a>
          );
        case 'venue_name':
          return row.venue_name || '—';
        case 'status': {
          const badge = STATUS_BADGES[row.status] || STATUS_BADGES.unmatched;
          return (
            <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>
              {row.status_label}
            </span>
          );
        }
        case 'toast_item_count':
          return (row.toast_item_count || 0).toLocaleString();
        case 'toast_net_sales':
          return cf.format(row.toast_net_sales || 0);
        case 'pnl_posted':
          return cf.format(row.pnl_posted || 0);
        case 'business_date':
          return <span className="text-nowrap">{row.business_date ? formatEventDate(row.business_date) : '—'}</span>;
        case 'last_synced_at':
          return <span className="text-nowrap">{row.last_synced_at ? formatEventDateTime(row.last_synced_at) : '—'}</span>;
        case 'actions': {
          if (row.importable) {
            const state = importState[`${row.venue_ownership_id}-${row.toast_date_path}`];
            if (state === 'queued') return <span className="small text-muted">Pull queued</span>;
            if (state === 'none') return <span className="small text-muted">Nothing to pull</span>;
            if (state === 'failed') return <span className="small text-danger">Pull failed</span>;

            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={state === 'pending'}
                onClick={() => importFile(row)}
              >
                {state === 'pending' ? 'Queuing…' : 'Pull from Toast'}
              </Button>
            );
          }

          if (!row.resyncable) return null;

          const state = syncState[row.id];
          if (state === 'queued') return <span className="small text-muted">Re-sync queued</span>;
          if (state === 'failed') return <span className="small text-danger">Re-sync failed</span>;

          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={state === 'pending'}
              onClick={() => resync(row.id)}
            >
              {state === 'pending' ? 'Queuing…' : 'Re-sync'}
            </button>
          );
        }
        default:
          return '—';
      }
    },
  };

  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithHandlers} />;
}

export default ReportingFoodAndBeverageToastSyncPage;
